import React, { useState } from 'react';
import {
  Button,
  FormWithRedirect,
  SaveButton,
  useNotify,
  useRefresh,
  useUpdate,
} from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import ButtonUI from '@material-ui/core/Button';
import CheckIcon from '@material-ui/icons/Check';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import IconCancel from '@material-ui/icons/Cancel';
import EditIcon from '@material-ui/icons/Edit';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import UnifiedEditTax from './UnifiedEditTax';
import UnifiedEditCustomDiscount from './UnifiedEditCustomDiscount';
import UnifiedEditCoupon from './UnifiedEditCoupon';
import UnifiedEditPaid from './UnifiedEditPaid';
import { formatPrice, getZ3PConfigValue, trackFullStoryEvent } from '../../helper';
import { apiPut } from '../../brainApi';

const useStyles = makeStyles((theme) => ({
  actionEdit: {
    textAlign: 'right',
    position: 'relative',
  },
  listLabel: {
    fontWeight: '600',
    color: '#637381',
  },
  basicEdit: {
    cursor: 'pointer',
    zIndex: 2,
    color: '#637381',
    width: 16.67,
    height: 16.67,
  },
  tableRoot: {
    overflowX: 'unset',
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  flexEnd: {
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  markPaidButton: {
    marginRight: '8px',
    borderRadius: 8,
    color: theme.palette.background.default,
    backgroundColor: '#FE5F10',
    border: `1px solid #FE5F10`,
    padding: '11px 16px',
    fontWeight: 600,
    '&:hover': {
      color: theme.palette.background.default,
      border: `1px solid #FE5F10`,
      backgroundColor: '#FE5F10',
    },
  },
  amountPaidDialog: {
    width: '20em',
    height: '5em',
  },
  tableCellSubTotal: {
    color: '#212B36',
    fontWeight: 700,
  },
  tableCellAmountDue: {
    color: '#B76E00',
    fontWeight: 700,
  },
  tableCellTax: {
    color: '#FF5630',
  },
  tableCellCommon: {
    color: '#212B36',
    float: 'right',
  },
  tableCellTotal: {
    color: '#212B36',
    fontSize: 16,
    fontWeight: 700,
  },
  btnCancel: {
    backgroundColor: '#fdfdfd',
    height: 37,
    marginRight: 10,
    padding: 10,
    color: '#212B36',
    border: '1px solid rgba(145, 158, 171, 0.24)',
    borderRadius: 8,
  },
  dollarIconStyle: {
    marginLeft: 8,
  },
  tableCellWidth: {
    width: '35%',
  },
}));

const UnifiedFinancial = (props) => {
  const { record, isPaidOrHeld } = props;
  const {
    id,
    amount_paid_dollars,
    coupon_code,
    coupon_amount_dollars,
    custom_discount_amount_dollars,
    amount_due,
    custom_discount_reason,
    minimum_service_charge_amount_dollars,
    subtotal_dollars,
    shop_fee_dollars,
    sales_tax_dollars,
    tax_rate_percent,
    total_taxed_price_dollars,
    tip_amount_dollars,
    services,
    st_order_id,
  } = record;
  const [editFinancial, setEditFinancial] = useState(false);
  const total_discount_dollars = (coupon_amount_dollars || 0) + (custom_discount_amount_dollars || 0);
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate('scheduled-work');
  const { is_accepting_tip: isAcceptingTip } = getZ3PConfigValue();
  const handleSubmit = async (values) => {
    update(
      { payload: { id, data: values, previousData: record } },
      {
        onSuccess: () => {
          setEditFinancial(false);
          notify('Financials updated successfully');
          refresh();
        },
        onFailure: ({ error }) => {
          notify(error.message, 'error');
        },
      },
    );
  };

  const openSimpleShare = () => {
    trackFullStoryEvent('CTA_View_Payment_SimpleShare_Redirection', {
      st_order_id,
      source: 'Financials',
    });
    window.parent.postMessage(
      `orderId=${st_order_id}`,
      window.location.host.includes('staging')
        ? 'https://simple360-temp-staging-branch-simpletire.vercel.app/'
        : 'https://app.simple360.com',
    );
  };

  const checkEditButton = (services = []) => {

    if (!Array.isArray(services) || services.length === 0) return null;
    if (services.length === 1) {
      if (services[0]?.display_name?.replace(/\s+/g, ' ').trim() === "New Tire Installation") {
        const status = services[0]?.service_payment_status;
        if (status === 'PaidInstall') {
          return true;
        }
        if (status === 'PayAtShop') {
          return false;
        }
      }
      if (services[0]?.display_name === "Tire Pick Up") {
        return true;
      }
    }
    // else {
    //     const allArePayAtShop = services.every(
    //         service => service?.service_payment_status === 'PayAtShop'
    //     );
    //     if (allArePayAtShop) {
    //         return "Customer has declined to pay online for services.";
    //     }
    // }

    return null;
  };

  const classes = useStyles();
  return (
    <>
      <FormWithRedirect
        resource="scheduled-work"
        record={record}
        save={handleSubmit}
        render={({ handleSubmitWithRedirect, loading, pristine }) => (
          <Box mt={3} className="finance_seaction">
            <Grid container spacing={0}>
              <Grid item xs={10} sm={10} md={10} />
              <Grid item xs={2} sm={2} md={2} className={classes.actionEdit}>
                {!editFinancial &&
                  !isPaidOrHeld &&
                  record.status != 'cancelled' && !checkEditButton(services) && (
                    <EditIcon
                      fontSize="inherit"
                      className={classes.basicEdit}
                      onClick={() => setEditFinancial((prev) => !prev)}
                    />
                  )}
              </Grid>
            </Grid>
            <TableContainer className={classes.tableRoot}>
              <Table className={classes.table} aria-label="simple table">
                <TableBody className="services-table-body">
                  {!st_order_id && (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className={classes.tableCellWidth}
                      />
                      <TableCell colSpan={3} align="right" className="f-wt-450">
                        <Box>
                          <Typography
                            variant="body1"
                            gutterBottom
                            className={classes.listLabel}
                          >
                            SubTotal
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        colSpan={6}
                        align="right"
                        className={classes.tableCellSubTotal}
                      >
                        {formatPrice(subtotal_dollars)}
                      </TableCell>
                    </TableRow>
                  )}
                  {!st_order_id && (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className={classes.tableCellWidth}
                      />
                      <TableCell colSpan={3} align="right" className="f-wt-450">
                        <Box>
                          <Typography
                            variant="body1"
                            gutterBottom
                            className={classes.listLabel}
                          >
                            Shop Fee
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        colSpan={6}
                        align="right"
                        className={classes.tableCellTax}
                      >
                        {formatPrice(shop_fee_dollars)}
                      </TableCell>
                    </TableRow>
                  )}
                  {!st_order_id && (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className={classes.tableCellWidth}
                      />
                      <TableCell colSpan={3} align="right" className="f-wt-450">
                        <SalesTax tax_rate_percent={tax_rate_percent} />
                      </TableCell>
                      <TableCell
                        align="right"
                        colSpan={6}
                        className={`${classes.tableCellTax}`}
                      >
                        <Grid
                          container
                          spacing={0}
                          className={`${classes.flexEnd} schedule_work_section`}
                        >
                          {editFinancial && <UnifiedEditTax />}
                          <Grid item md={editFinancial ? 4 : 12}>
                            {formatPrice(sales_tax_dollars)}
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  )}
                  {!st_order_id && !!minimum_service_charge_amount_dollars && (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className={classes.tableCellWidth}
                      />
                      <TableCell colSpan={3} align="right">
                        <MinimumServiceCharge />
                      </TableCell>
                      <TableCell
                        colSpan={6}
                        align="right"
                        className={classes.tableCellTax}
                      >
                        {formatPrice(minimum_service_charge_amount_dollars)}
                      </TableCell>
                    </TableRow>
                  )}
                  {!st_order_id && isAcceptingTip && (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className={classes.tableCellWidth}
                      />
                      <TableCell colSpan={3} align="right" className="f-wt-450">
                        <Typography
                          variant="body1"
                          gutterBottom
                          className={classes.listLabel}
                        >
                          Tip
                        </Typography>
                      </TableCell>
                      <TableCell
                        colSpan={6}
                        align="right"
                        className={classes.tableCellCommon}
                      >
                        {formatPrice(tip_amount_dollars)}
                      </TableCell>
                    </TableRow>
                  )}
                  {!st_order_id && (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className={classes.tableCellWidth}
                      />
                      <TableCell colSpan={3} align="right" className="f-wt-450">
                        <Typography
                          variant="body1"
                          gutterBottom
                          className={classes.listLabel}
                        >
                          Coupon code {coupon_code ? `- ${coupon_code}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell
                        colSpan={6}
                        align="right"
                        className={`${classes.tableCellCommon}`}
                      >
                        <Grid container spacing={0} className={classes.flexEnd}>
                          {editFinancial && <UnifiedEditCoupon {...props} />}
                          <Grid item md={editFinancial ? 4 : 12}>
                            {coupon_amount_dollars
                              ? `- $${coupon_amount_dollars.toFixed(2)}`
                              : '-'}
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  )}
                  {!st_order_id && (
                    <TableRow>
                      {editFinancial ? (
                        <UnifiedEditCustomDiscount
                          custom_discount_amount_dollars={
                            custom_discount_amount_dollars
                          }
                          showFormatted
                        />
                      ) : (
                        <CustomDiscount
                          custom_discount_reason={custom_discount_reason}
                          custom_discount_amount_dollars={
                            custom_discount_amount_dollars
                          }
                        />
                      )}
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell colSpan={3} className={classes.tableCellWidth} />
                    <TableCell colSpan={3} align="right">
                      <Box>
                        <Typography
                          variant="body1"
                          gutterBottom
                          className={classes.tableCellTotal}
                        >
                          Sub Total
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      colSpan={6}
                      align="right"
                      className={classes.tableCellTotal}
                    >
                      {formatPrice(subtotal_dollars)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={3} className={classes.tableCellWidth} />
                    <TableCell colSpan={3} align="right">
                      <Box>
                        <Typography
                          variant="body1"
                          gutterBottom
                          className={classes.tableCellTotal}
                        >
                          Sales Tax
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      colSpan={6}
                      align="right"
                      className={classes.tableCellTotal}
                    >
                      {formatPrice(sales_tax_dollars)}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={3} className={classes.tableCellWidth} />
                    <TableCell colSpan={3} align="right">
                      <Box>
                        <Typography
                          variant="body1"
                          gutterBottom
                          className={classes.tableCellTotal}
                        >
                          Total
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      colSpan={6}
                      align="right"
                      className={classes.tableCellTotal}
                    >
                      {formatPrice(total_taxed_price_dollars)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={3} className={classes.tableCellWidth} />
                    <TableCell colSpan={3} align="right">
                      <Box>
                        <Typography
                          variant="body1"
                          gutterBottom
                          className={classes.tableCellTotal}
                        >
                          Discount
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      colSpan={6}
                      align="right"
                      className={classes.tableCellTotal}
                    >
                      {total_discount_dollars > 0 ? `-${formatPrice(total_discount_dollars)}` : formatPrice(0)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {editFinancial ? (
                      <UnifiedEditPaid
                        amount_paid_dollars={amount_paid_dollars}
                        id={id}
                        {...props}
                      />
                    ) : (
                      <AmountPaidDollars
                        amount_paid_dollars={amount_paid_dollars}
                        total_taxed_price_dollars={total_taxed_price_dollars}
                        id={id}
                        st_order_id={st_order_id}
                      />
                    )}
                  </TableRow>
                </TableBody>
                <AmountDue
                  custom_discount_reason={custom_discount_reason}
                  custom_discount_amount_dollars={
                    amount_due
                  }
                />
              </Table>

            </TableContainer>
            {editFinancial && (
              <Grid item xs={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  ml={2}
                  my={2.5}
                >
                  <Button
                    label="ra.action.cancel"
                    onClick={() => setEditFinancial(false)}
                    className={classes.btnCancel}
                  >
                    <IconCancel />
                  </Button>
                  <SaveButton
                    handleSubmitWithRedirect={handleSubmitWithRedirect}
                    saving={loading}
                    disabled={pristine}
                  />
                </Box>
              </Grid>
            )}
          </Box>
        )}
      />
    </>
  );
};

export default UnifiedFinancial;

const SalesTax = ({ tax_rate_percent }) => {
  const classes = useStyles();
  return (
    <Box>
      <Typography variant="body1" gutterBottom className={classes.listLabel}>
        {`Sales Tax ${tax_rate_percent ? `- ${tax_rate_percent}%` : ''}`}
      </Typography>
    </Box>
  );
};

const MinimumServiceCharge = () => {
  const classes = useStyles();
  return (
    <Box display="flex" alignItems="center">
      <Typography variant="body1" gutterBottom className={classes.listLabel}>
        Minimum Service Charge
      </Typography>
    </Box>
  );
};

const CustomDiscount = ({
  custom_discount_reason,
  custom_discount_amount_dollars,
}) => {
  const classes = useStyles();
  let customDiscountAmountDollars = '-';
  if (custom_discount_amount_dollars) {
    customDiscountAmountDollars = `- $${custom_discount_amount_dollars.toFixed(
      2,
    )}`;
  }
  return (
    <>
      <TableCell colSpan={3} className={classes.tableCellWidth} />
      <TableCell colSpan={3} align="right" className="f-wt-450">
        <Box>
          <Typography
            variant="body1"
            gutterBottom
            className={classes.listLabel}
          >
            {`Custom Discount ${custom_discount_reason ? `- ${custom_discount_reason}` : ''
              }`}
          </Typography>
        </Box>
      </TableCell>
      <TableCell colSpan={6} align="right" className={classes.tableCellCommon}>
        {customDiscountAmountDollars}
      </TableCell>
    </>
  );
};

const AmountDue = ({
  custom_discount_reason,
  custom_discount_amount_dollars,
}) => {
  const classes = useStyles();
  let customDiscountAmountDollars = '-';
  if (custom_discount_amount_dollars) {
    customDiscountAmountDollars = `$${custom_discount_amount_dollars}`;
  }
  return (
    <>
      <TableCell colSpan={3} className={classes.tableCellWidth} />
      <TableCell colSpan={3} align="right" className="f-wt-450">
        <Box>
          <Typography
            variant="body1"
            gutterBottom
            className={classes.listLabel}
          >
            Amount due
          </Typography>
        </Box>
      </TableCell>
      <TableCell colSpan={6} align="right" className={classes.tableCellAmountDue}>
        {customDiscountAmountDollars}
      </TableCell>
    </>
  );
};

const AmountPaidDollars = ({
  amount_paid_dollars,
  total_taxed_price_dollars,
  id,
  st_order_id,
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();
  const [openMarkAsPaidModal, setOpenMarkAsPaidModal] = useState(false);
  const [sendReceipt, setSendReceipt] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const payload = {
    amount_paid_dollars: `${total_taxed_price_dollars}`,
    send_receipt: sendReceipt,
    status: 'finished',
  };

  const completeRepairOrder = (st_order_id = null) => {
    if (st_order_id) {
      handlePaidAmount(st_order_id);
    } else {
      setOpenMarkAsPaidModal(true);
    }
  };

  const handlePaidAmount = (st_order_id = null) => {
    setIsLoading(true);
    if (st_order_id) {
      payload.send_receipt = false;
    }
    apiPut(`/scheduled-work/mark-as-paid/${id}`, payload)
      .then((response) => {
        setOpenMarkAsPaidModal(false);
        setSendReceipt(true);
        setIsLoading(false);
        if (response.Status === 'Success') {
          notify(response.Message);
          refresh();
        } else {
          notify(response.Message, 'warning');
        }
      })
      .catch((error) => {
        setOpenMarkAsPaidModal(false);
        setSendReceipt(true);
        setIsLoading(false);
        notify(`Error: ${error}`, 'warning');
      });
  };

  return (
    <>
      <Dialog open={openMarkAsPaidModal}>
        <DialogContent className={classes.amountPaidDialog}>
          <div>
            Set Amount Paid to {formatPrice(total_taxed_price_dollars)}?
          </div>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={sendReceipt}
                onChange={() => setSendReceipt(!sendReceipt)}
              />
            }
            label="Send Receipt To Customer"
          />
        </DialogContent>
        <DialogActions>
          <ButtonUI
            onClick={() => setOpenMarkAsPaidModal(false)}
            color="primary"
          >
            Cancel
          </ButtonUI>
          <ButtonUI
            color="primary"
            variant="contained"
            onClick={() => handlePaidAmount()}
          >
            {!isLoading && <CheckIcon style={{ marginRight: '7px' }} />}
            {isLoading ? 'Updating...' : 'Confirm'}
          </ButtonUI>
        </DialogActions>
      </Dialog>
      <TableCell colSpan={3} className={classes.tableCellWidth} />
      <TableCell colSpan={3} align="right" className="f-wt-450">
        <Box>
          <Typography
            variant="body1"
            gutterBottom
            className={classes.listLabel}
          >
            Paid
          </Typography>
        </Box>
      </TableCell>
      <TableCell colSpan={6} align="right" className={classes.tableCellCommon}>
        <Box display="flex" alignItems="center">
          {/* {amount_paid_dollars !== total_taxed_price_dollars && (
            <Button
              label=""
              onClick={() => completeRepairOrder(st_order_id)}
              className={classes.markPaidButton}
            >
            <>
              Mark as Complete
               <svg className={classes.dollarIconStyle} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M14.8 9C14.6188 8.68577 14.3557 8.42671 14.0386 8.25056C13.7215 8.07441 13.3625 7.9878 13 8H11C10.4696 8 9.96086 8.21072 9.58579 8.58579C9.21071 8.96086 9 9.46957 9 10C9 10.5304 9.21071 11.0391 9.58579 11.4142C9.96086 11.7893 10.4696 12 11 12H13C13.5304 12 14.0391 12.2107 14.4142 12.5858C14.7893 12.9609 15 13.4696 15 14C15 14.5304 14.7893 15.0391 14.4142 15.4142C14.0391 15.7893 13.5304 16 13 16H11C10.6375 16.0122 10.2785 15.9256 9.96142 15.7495C9.64435 15.5733 9.38115 15.3142 9.2 15M12 7V17M3 12C3 13.1819 3.23279 14.3522 3.68508 15.4442C4.13738 16.5361 4.80031 17.5282 5.63604 18.364C6.47177 19.1997 7.46392 19.8626 8.55585 20.3149C9.64778 20.7672 10.8181 21 12 21C13.1819 21 14.3522 20.7672 15.4442 20.3149C16.5361 19.8626 17.5282 19.1997 18.364 18.364C19.1997 17.5282 19.8626 16.5361 20.3149 15.4442C20.7672 14.3522 21 13.1819 21 12C21 10.8181 20.7672 9.64778 20.3149 8.55585C19.8626 7.46392 19.1997 6.47177 18.364 5.63604C17.5282 4.80031 16.5361 4.13738 15.4442 3.68508C14.3522 3.23279 13.1819 3 12 3C10.8181 3 9.64778 3.23279 8.55585 3.68508C7.46392 4.13738 6.47177 4.80031 5.63604 5.63604C4.80031 6.47177 4.13738 7.46392 3.68508 8.55585C3.23279 9.64778 3 10.8181 3 12Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </>
            </Button>
          )} */}
          {formatPrice(amount_paid_dollars)}
        </Box>
      </TableCell>
    </>
  );
};
